import {
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useId,
  useRef,
  useState,
} from 'react';

import { cn } from '@shared/lib/cn';
import { Spinner } from '@shared/ui/spinner';

import { Tab } from './tab.ui';
import type { TabSize, TabsStatus } from './tabs.constant';
import { TabsSkeleton } from './tabs.skeleton.ui';

export interface TabsItem {
  /** 탭을 가리키는 값(`value` · `onValueChange` 에 쓰인다) */
  value: string;
  /** Figma `_Tabs/Tab` 의 `Label` */
  label: ReactNode;
  /** Figma `Panel`. 이 탭이 선택됐을 때 아래에 그리는 내용. 없으면 탭 줄만 그린다 */
  panel?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'defaultValue' | 'onChange'
> {
  /** 탭 목록(Figma 의 `_Tabs/Tab` ×N) */
  items: TabsItem[];
  /** 선택된 탭(제어). 비제어는 `defaultValue`(없으면 누를 수 있는 첫 탭) */
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** 탭 크기(`_Tabs/Tab` 의 `size`). 기본 md */
  size?: TabSize;
  /** Figma `status`. init = 스켈레톤, loading = 패널을 흐리게 + Spinner */
  status?: TabsStatus;
  /** 탭 줄(`role="tablist"`)의 이름 */
  'aria-label'?: string;
  className?: string;
}

/**
 * 탭 줄 + 선택된 탭의 패널. 좌우 화살표 · Home · End 로 탭을 옮기고 곧바로 선택한다(자동 선택).
 * 선택된 탭만 Tab 순서에 든다(roving tabindex).
 */
export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  size = 'md',
  status = 'default',
  'aria-label': ariaLabel,
  className,
  ...rest
}: TabsProps) {
  const baseId = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [innerValue, setInnerValue] = useState(
    () => defaultValue ?? items.find((item) => !item.disabled)?.value,
  );
  const current = value ?? innerValue;

  if (status === 'init') {
    return (
      <TabsSkeleton
        count={items.length || undefined}
        showPanel={items.some((item) => item.panel !== undefined)}
        className={className}
      />
    );
  }

  const loading = status === 'loading';
  const activeIndex = items.findIndex((item) => item.value === current);
  const activeItem = activeIndex >= 0 ? items[activeIndex] : undefined;
  const focusIndex = activeIndex >= 0 ? activeIndex : items.findIndex((item) => !item.disabled);

  const select = (next: string) => {
    if (next === current) return;
    if (value === undefined) setInnerValue(next);
    onValueChange?.(next);
  };

  const moveTo = (index: number) => {
    const item = items[index];
    if (!item) return;
    tabRefs.current[index]?.focus();
    select(item.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const enabled = items.flatMap((item, index) => (item.disabled ? [] : [index]));
    if (enabled.length === 0) return;
    const at = enabled.indexOf(focusIndex);
    let target: number | undefined;
    if (event.key === 'ArrowRight') target = enabled[(at + 1) % enabled.length];
    else if (event.key === 'ArrowLeft')
      target = enabled[(at - 1 + enabled.length) % enabled.length];
    else if (event.key === 'Home') target = enabled[0];
    else if (event.key === 'End') target = enabled[enabled.length - 1];
    if (target === undefined) return;
    event.preventDefault();
    moveTo(target);
  };

  const tabId = (index: number) => `${baseId}-tab-${index}`;
  const panelId = (index: number) => `${baseId}-panel-${index}`;
  const hasPanel = activeItem?.panel !== undefined;

  return (
    <div
      data-slot="tabs"
      data-size={size}
      data-status={status}
      aria-busy={loading || undefined}
      {...rest}
      className={cn('relative flex flex-col gap-4', className)}
    >
      <div
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
        className={cn(
          'flex items-start border-b border-border-subtle',
          loading && !hasPanel && 'opacity-(--opacity-disabled)',
        )}
      >
        {items.map((item, index) => {
          const active = index === activeIndex;
          return (
            <Tab
              key={item.value}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              id={tabId(index)}
              size={size}
              active={active}
              disabled={item.disabled}
              aria-controls={active && hasPanel ? panelId(index) : undefined}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => select(item.value)}
            >
              {item.label}
            </Tab>
          );
        })}
      </div>
      {hasPanel ? (
        <div
          role="tabpanel"
          id={panelId(activeIndex)}
          aria-labelledby={tabId(activeIndex)}
          tabIndex={0}
          className={cn('relative', loading && 'pointer-events-none')}
        >
          <div className={cn(loading && 'opacity-(--opacity-disabled)')}>{activeItem?.panel}</div>
          {loading ? (
            <Spinner size="md" data-slot="spinner" className="absolute inset-0 m-auto" />
          ) : null}
        </div>
      ) : loading ? (
        <Spinner size="md" data-slot="spinner" className="absolute inset-0 m-auto" />
      ) : null}
    </div>
  );
}
